import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { ChevronLeft } from 'lucide-react';

import { GET_ME, GET_WORKOUT_LOGS_STATS } from '../../graphql/queries';
import { trackEvent } from '../../utils/gt';
import { isTabRoot } from '../../utils/pwa';
import { computeStreakDays } from '../../utils/workoutStats';
import { isProAccount } from '../../utils/workoutLock';

// A page's own back keeps its logic (a parent view, a shared link's way up): the round button presses it. The CSS hides
// it in the installed app
const PAGE_BACK = '.mt-editor-bar__back, .mt-pg-back';

/**
 * The installed app's header on a phone (owner decision 30/10/2026): no bar, round glass buttons over the page, in the
 * old header's box, so everything measured from it holds. Back on the left of inner pages, going home when it's the
 * first page of the visit; the avatar on the right of every page, with the streak beside it on the home, its door to
 * Progresso. Conta is where the avatar leads, so it has none.
 */
export const FloatingHeader = () => {
  const { t } = useTranslation();
  const { pathname, key } = useLocation();
  const navigate = useNavigate();
  const isSignedIn = !!localStorage.getItem('authToken');
  const isHome = pathname === '/';
  // Both already loaded by the page underneath
  const { data } = useQuery(GET_ME, {
    fetchPolicy: 'cache-first',
    skip: !isSignedIn,
  });
  const { data: dataLogs } = useQuery(GET_WORKOUT_LOGS_STATS, {
    fetchPolicy: 'cache-first',
    skip: !isSignedIn || !isHome,
  });
  const me = data?.me;
  const initial = (me?.name || '?').trim().charAt(0).toUpperCase();
  const streak = useMemo(
    () => computeStreakDays(dataLogs?.workoutLogs?.data || []),
    [dataLogs]
  );
  // Under the buttons, the page dissolves into a fade once it scrolls
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 4);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const goBack = () => {
    trackEvent('app_header_back', 'App Header - Back', 'app_header', true);
    const pageBack = document.querySelector(PAGE_BACK);
    if (pageBack) pageBack.click();
    else if (key === 'default') navigate('/', { replace: true });
    else navigate(-1);
  };

  return (
    <header
      className={`mt-root mt-app-header mt-app-header--floating${isScrolled ? ' is-scrolled' : ''}`}
    >
      <div className="mt-app-header__inner">
        {!isTabRoot(pathname) && (
          <button
            type="button"
            className="mt-round"
            onClick={goBack}
            aria-label={t('standalone.goBack')}
          >
            <ChevronLeft aria-hidden="true" />
          </button>
        )}
        <span className="mt-app-header__actions">
          {isHome && streak > 0 && (
            <Link
              className="mt-round mt-round--pill"
              to="/progress"
              aria-label={t('pages.Dashboard.app.streakLabel', {
                count: streak,
              })}
              onClick={() =>
                trackEvent(
                  'dashboard_stat_streak',
                  'Dashboard - stat_streak',
                  'dashboard',
                  true
                )
              }
            >
              {t('pages.Dashboard.app.streak', { count: streak })}
            </Link>
          )}
          {isSignedIn && pathname !== '/account' && (
            <Link
              className="mt-round"
              to="/account"
              aria-label={t('pages.Dashboard.home.avatarLabel')}
            >
              <span className="mt-round__avatar">
                {initial}
                {isProAccount(me) && (
                  <span className="mt-app-header__pro" aria-hidden="true">
                    {t('pages.Dashboard.proPlan')}
                  </span>
                )}
              </span>
            </Link>
          )}
        </span>
      </div>
    </header>
  );
};
